/// <amd-module name="TFS/Samples/ThemeGenerator/ThemeGeneratorView" />
import * as React from "react";

import { Fabric } from "OfficeFabric/Fabric";
import * as Resources from "TFS/Samples/ThemeGenerator/Resources";
import { ITfsThemeService } from "TFS/ThemeManagement/TfsThemeService";
import { ObservableValue } from "VSS/Core/Observable";
import { ThemeData } from "VSS/Core/Util/Theming";
import { Components, IVssComponentContext, VssComponent } from "VSS/Platform/Layout";
import { IVssPerformanceService } from "VSS/Platform/Performance";
import { ClientTheme, IVssThemeService } from "VSS/Platform/Theming";
import { Card } from "VSSUI/Card";
import { ConditionalChildren } from "VSSUI/ConditionalChildren";
import { DropdownFilterBarItem } from "VSSUI/Dropdown";
import { FilterBar } from "VSSUI/FilterBar";
import { Header, TitleSize } from "VSSUI/Header";
import { getFullScreenItem, HeaderCommandBarWithFilter, toggleFullScreen } from "VSSUI/HeaderCommandBar";
import { IListSelection } from "VSSUI/List";
import { IListBoxItem } from "VSSUI/ListBox";
import { VssHubViewState } from "VSSUI/Navigation/VssHubViewState";
import { Observer } from "VSSUI/Observer";
import { Page } from "VSSUI/Page";
import { IPickListItem, IPickListSelection, PickListDropdown } from "VSSUI/PickList";
import { Surface, SurfaceBackground } from "VSSUI/Surface";
import { ITabProviderContext, Tab, TabBar, TabProviderContext } from "VSSUI/Tabs";
import { KeywordFilterBarItem } from "VSSUI/TextFilterBarItem";
import { Toggle } from "VSSUI/Toggle";
import { DropdownMultiSelection } from "VSSUI/Utilities/DropdownSelection";
import { ColorsGrid, IColorsGridColumn } from "./ColorEditorGrid";
import { darkenPaletteColor, lightenPaletteColor } from "./ColorUtil";

const themeGeneratorId = "my-custom-theme";

interface IThemeGeneratorProps {}

interface IThemeGeneratorState {
    generatedText: string;
    themes?: ClientTheme[];
    baseTheme?: ClientTheme;
    userThemeData: ThemeData;
    hubViewState: VssHubViewState;
    autoComputeShades: boolean;
    filterText?: string;
}

class ThemeGenerator extends VssComponent<IThemeGeneratorProps, IThemeGeneratorState> {
    private loggedPerfEvent: boolean;
    private filterToggled: ObservableValue<boolean> = new ObservableValue<boolean>(false);
    private fullScreenToggled: ObservableValue<boolean> = new ObservableValue<boolean>(false);
    private groupSelection: IListSelection;

    private customGridColumns: IColorsGridColumn[] = [
        {
            groups: [
                {
                    name: Resources.HeaderText,
                    propertyNamePrefix: "text-"
                },
                {
                    name: Resources.HeaderBackground,
                    propertyNamePrefix: "background-"
                },
                {
                    name: Resources.HeaderCommunication,
                    propertyNamePrefix: "communication-"
                },
                {
                    name: Resources.HeaderCallout,
                    propertyNamePrefix: "callout-"
                },
                {
                    name: Resources.HeaderBorder,
                    propertyNamePrefix: "border-"
                },
                {
                    name: Resources.HeaderIcons,
                    propertyNamePrefix: "icon-"
                },
                {
                    name: Resources.HeaderPanel,
                    propertyNamePrefix: "panel-"
                },
                {
                    name: Resources.HeaderStatusColors,
                    propertyNamePrefix: "status-"
                },
                {
                    name: Resources.HeaderComponents,
                    propertyNamePrefix: "component-"
                }
            ]
        },
        {
            groups: [
                {
                    name: Resources.HeaderNavigation,
                    propertyNamePrefix: "nav-"
                }
            ]
        }
    ];

    constructor(props: IThemeGeneratorProps, context: IVssComponentContext) {
        super(props, context);

        this.groupSelection = new DropdownMultiSelection();

        this.state = {
            generatedText: "",
            themes: [],
            userThemeData: {},
            autoComputeShades: true,
            hubViewState: new VssHubViewState(context.pageContext, {
                defaultPivot: "palette"
            })
        };
    }

    public componentDidMount(): void {
        const themeService = this.context.pageContext.getService<ITfsThemeService>("ITfsThemeService");
        themeService.getThemes().then(themes => {
            const currentTheme = this.context.pageContext.getService<IVssThemeService>("IVssThemeService").getCurrentTheme();

            let currentThemeData: ThemeData | undefined;
            let baseTheme: ClientTheme | undefined;

            if (currentTheme && currentTheme.id === themeGeneratorId) {
                const customTheme = this.context.pageContext.getService<IVssThemeService>("IVssThemeService").getCurrentTheme();
                if (customTheme) {
                    baseTheme = themes.find(t => t.id === customTheme.extends);
                    if (baseTheme) {
                        currentThemeData = customTheme.data;
                    }
                }
            }

            if (!baseTheme && currentTheme) {
                baseTheme = themes.find(t => t.id === currentTheme.id);
            }

            if (!baseTheme) {
                baseTheme = themes[0];
            }

            if (!currentThemeData) {
                currentThemeData = baseTheme.data;
            }

            this.setState({
                themes,
                baseTheme,
                userThemeData: { ...currentThemeData },
                generatedText: this.generateExtensionJsonText(currentThemeData, baseTheme)
            });
        });
    }

    public componentDidUpdate(prevProps: IThemeGeneratorProps, prevState: IThemeGeneratorState): void {
        super.componentDidUpdate(prevProps, prevState);

        if (!this.loggedPerfEvent && this.state.baseTheme) {
            const perfService = this.context.pageContext.getService<IVssPerformanceService>("IVssPerformanceService");
            perfService.markTimeToInteractive("tfs", "WebPlatform.Samples.ThemeGenerator");
            this.loggedPerfEvent = true;
        }
    }

    public render(): JSX.Element {
        const { baseTheme, userThemeData, autoComputeShades } = this.state;
        const baseThemeData = baseTheme ? baseTheme.data : {};

        const paletteGridColumns: IColorsGridColumn[] = [
            {
                groups: [
                    {
                        name: Resources.HeaderPrimary,
                        propertyNamePrefix: "palette-primary",
                        mainColorPropertyName: "palette-primary",
                        computeShades: value => {
                            if (baseTheme && baseTheme.isDark) {
                                this.changeThemeValues({
                                    "palette-primary-shade-30": lightenPaletteColor(value, 0.45),
                                    "palette-primary-shade-20": lightenPaletteColor(value, 0.65),
                                    "palette-primary-shade-10": lightenPaletteColor(value, 0.85),
                                    "palette-primary": value,
                                    "palette-primary-darkened-6": lightenPaletteColor(value, 0.06),
                                    "palette-primary-darkened-10": lightenPaletteColor(value, 0.1),
                                    "palette-primary-tint-10": darkenPaletteColor(value, 0.85),
                                    "palette-primary-tint-20": darkenPaletteColor(value, 0.75),
                                    "palette-primary-tint-30": darkenPaletteColor(value, 0.57),
                                    "palette-primary-tint-40": darkenPaletteColor(value, 0.35)
                                });
                            } else {
                                this.changeThemeValues({
                                    "palette-primary-shade-30": darkenPaletteColor(value, 0.57),
                                    "palette-primary-shade-20": darkenPaletteColor(value, 0.75),
                                    "palette-primary-shade-10": darkenPaletteColor(value, 0.9),
                                    "palette-primary": value,
                                    "palette-primary-darkened-6": darkenPaletteColor(value, 0.06),
                                    "palette-primary-darkened-10": darkenPaletteColor(value, 0.1),
                                    "palette-primary-tint-10": lightenPaletteColor(value, 0.85),
                                    "palette-primary-tint-20": lightenPaletteColor(value, 0.22),
                                    "palette-primary-tint-30": lightenPaletteColor(value, 0.14),
                                    "palette-primary-tint-40": lightenPaletteColor(value, 0.07)
                                });
                            }
                        }
                    }
                ]
            },
            {
                groups: [
                    {
                        name: Resources.HeaderNeutrals,
                        propertyNamePrefix: "palette-neutral"
                    }
                ]
            },
            {
                groups: [
                    {
                        name: Resources.HeaderAccent1,
                        propertyNamePrefix: "palette-accent1",
                        mainColorPropertyName: "palette-accent1",
                        computeShades: value => {
                            if (baseTheme && baseTheme.isDark) {
                                this.changeThemeValues({
                                    "palette-accent1-dark": lightenPaletteColor(value, 0.1),
                                    "palette-accent1": value,
                                    "palette-accent1-light": darkenPaletteColor(value, 0.8)
                                });
                            } else {
                                this.changeThemeValues({
                                    "palette-accent1-dark": darkenPaletteColor(value, 0.8),
                                    "palette-accent1": value,
                                    "palette-accent1-light": lightenPaletteColor(value, 0.1)
                                });
                            }
                        }
                    },
                    {
                        name: Resources.HeaderAccent2,
                        propertyNamePrefix: "palette-accent2",
                        mainColorPropertyName: "palette-accent2",
                        computeShades: value => {
                            if (baseTheme && baseTheme.isDark) {
                                this.changeThemeValues({
                                    "palette-accent2-dark": lightenPaletteColor(value, 0.1),
                                    "palette-accent2": value,
                                    "palette-accent2-light": darkenPaletteColor(value, 0.8)
                                });
                            } else {
                                this.changeThemeValues({
                                    "palette-accent2-dark": darkenPaletteColor(value, 0.8),
                                    "palette-accent2": value,
                                    "palette-accent2-light": lightenPaletteColor(value, 0.1)
                                });
                            }
                        }
                    },
                    {
                        name: Resources.HeaderAccent3,
                        propertyNamePrefix: "palette-accent3",
                        mainColorPropertyName: "palette-accent3",
                        computeShades: value => {
                            if (baseTheme && baseTheme.isDark) {
                                this.changeThemeValues({
                                    "palette-accent3-dark": lightenPaletteColor(value, 0.1),
                                    "palette-accent3": value,
                                    "palette-accent3-light": darkenPaletteColor(value, 0.8)
                                });
                            } else {
                                this.changeThemeValues({
                                    "palette-accent3-dark": darkenPaletteColor(value, 0.8),
                                    "palette-accent3": value,
                                    "palette-accent3-light": lightenPaletteColor(value, 0.1)
                                });
                            }
                        }
                    }
                ]
            }
        ];

        return (
            <Fabric className="flex-grow flex-row">
                <Surface background={SurfaceBackground.neutral}>
                    <Page selectedTabId={this.state.hubViewState.selectedPivot} className="flex-grow theme-generator-container">
                        <Header className="flex-noshrink" title={Resources.ThemeGeneratorTitle} titleSize={TitleSize.Medium} />
                        <TabBar renderAdditionalContent={this.renderAdditionalContent} onSelectedTabChanged={this.onSelectedTabChanged}>
                            <Tab id="palette" name={Resources.PalettePivot} />
                            <Tab id="map" name={Resources.CustomizationsPivot} />
                            <Tab id="generate" name={Resources.ExtensionContent} />
                        </TabBar>
                        <TabProviderContext.Consumer>
                            {(tabProviderContext: ITabProviderContext) => {
                                if (tabProviderContext.selectedId === "palette") {
                                    return (
                                        <div className="page-content page-content-top">
                                            <Card className="flex-grow">
                                                <div>
                                                    <div className="theme-picklist-container flex-row flex-center">
                                                        <label>{Resources.BaseTheme}</label>
                                                        <PickListDropdown
                                                            className="theme-picklist"
                                                            onSelectionChanged={this.onThemeSelected}
                                                            width={180}
                                                            getPickListItems={() => this.state.themes || []}
                                                            getListItem={this.getPickListItem}
                                                            selectedItems={baseTheme ? [baseTheme] : []}
                                                        />
                                                        <label className="auto-shades-label">{Resources.AutoComputeShades}</label>
                                                        <Toggle
                                                            checked={autoComputeShades}
                                                            onChange={this.onAutoComputeShadesChanged}
                                                            onText={Resources.On}
                                                            offText={Resources.Off}
                                                        />
                                                    </div>
                                                    <ColorsGrid
                                                        autoComputeShades={autoComputeShades}
                                                        isPaletteColors={true}
                                                        themeData={userThemeData}
                                                        baseThemeData={baseThemeData}
                                                        onChanged={this.onValueChanged}
                                                        columns={paletteGridColumns}
                                                    />
                                                </div>
                                            </Card>
                                        </div>
                                    );
                                } else if (tabProviderContext.selectedId === "map") {
                                    return (
                                        <>
                                            <ConditionalChildren renderChildren={this.filterToggled}>{this.renderFilterBar()}</ConditionalChildren>
                                            <div className="page-content page-content-top">
                                                <Card className="flex-grow bolt-card-white">
                                                    <ColorsGrid
                                                        filter={this.state.hubViewState.filter}
                                                        className="colors-map"
                                                        isPaletteColors={false}
                                                        themeData={userThemeData}
                                                        baseThemeData={baseThemeData}
                                                        onChanged={this.onValueChanged}
                                                        columns={this.customGridColumns}
                                                    />
                                                </Card>
                                            </div>
                                        </>
                                    );
                                } else {
                                    return <pre className="page-content page-content-top">{this.state.generatedText}</pre>;
                                }
                            }}
                        </TabProviderContext.Consumer>
                    </Page>
                </Surface>
            </Fabric>
        );
    }

    private onSelectedTabChanged = (newTabId: string) => {
        this.state.hubViewState.selectedPivot.value = newTabId;
    };

    private renderAdditionalContent = () => {
        return this.state.hubViewState.selectedPivot.value === "map" ? (
            <Observer fullScreen={this.fullScreenToggled}>
                {(props: { fullScreen: boolean }) => (
                    <HeaderCommandBarWithFilter
                        items={[getFullScreenItem(this.onFullScreenToggled, props.fullScreen)]}
                        filter={this.state.hubViewState.filter}
                        filterToggled={this.filterToggled}
                    />
                )}
            </Observer>
        ) : (
            undefined
        );
    };

    private renderFilterBar = () => {
        return (
            <FilterBar
                className="bolt-filterbar-white depth-8 flex-noshrink"
                filter={this.state.hubViewState.filter}
                onDismissClicked={this.onFilterToggled}
            >
                <KeywordFilterBarItem filterItemKey="keyword" filter={this.state.hubViewState.filter} />
                <DropdownFilterBarItem
                    filterItemKey="group"
                    filter={this.state.hubViewState.filter}
                    items={this.getGroupPickListItems()}
                    selection={this.groupSelection}
                    placeholder="Group"
                />
            </FilterBar>
        );
    };

    private getGroupPickListItems = () => {
        const groupNames: IListBoxItem<{}>[] = [];
        for (const column of this.customGridColumns) {
            for (const group of column.groups) {
                groupNames.push({ text: group.name, id: group.name });
            }
        }

        return groupNames;
    };

    private onFilterToggled = () => {
        this.filterToggled.value = !this.filterToggled.value;
    };

    private onFullScreenToggled = () => {
        this.fullScreenToggled.value = !this.fullScreenToggled.value;
        toggleFullScreen(this.fullScreenToggled.value);
    };

    private onThemeSelected = (selection: IPickListSelection) => {
        if (selection.selectedItems.length) {
            const theme = selection.selectedItems[0];
            this.updateCurrentPageTheme(theme.data, theme, true);
            this.setState({
                baseTheme: theme,
                userThemeData: { ...theme.data },
                generatedText: this.generateExtensionJsonText({}, theme)
            });
        }
    };

    private changeThemeValues(values: { [key: string]: string }): void {
        const userThemeData = { ...this.state.userThemeData };
        for (const key in values) {
            userThemeData[key] = values[key];
        }
        this.updateCurrentPageTheme(userThemeData, this.state.baseTheme, false);
        this.setState({
            userThemeData,
            generatedText: this.generateExtensionJsonText(userThemeData, this.state.baseTheme)
        });
    }

    private onValueChanged = (key: string, newValue: string): void => {
        const values: { [key: string]: string } = {};
        values[key] = newValue;
        this.changeThemeValues(values);
    };

    private updateCurrentPageTheme(userThemeData: { [key: string]: string }, baseTheme: ClientTheme | undefined, isInitialState: boolean): void {
        const themeService = this.context.pageContext.getService<ITfsThemeService>("ITfsThemeService");
        themeService.applyTheme(
            {
                id: themeGeneratorId,
                name: Resources.CustomThemeName,
                extends: baseTheme ? baseTheme.id : "",
                data: userThemeData,
                isDark: !!baseTheme && baseTheme.isDark,
                isPreview: !!baseTheme && baseTheme.isPreview
            },
            !isInitialState
        );
    }

    private getPickListItem = (item: ClientTheme): IPickListItem => {
        return { name: item.name, key: item.id };
    };

    private generateExtensionJsonText(userThemeData: ThemeData, selectedTheme: ClientTheme | undefined): string {
        const extensionData: { [key: string]: string } = {};
        const themeData = selectedTheme ? selectedTheme.data : {};
        for (const key in userThemeData) {
            if (!(key in themeData) || userThemeData[key] !== themeData[key]) {
                extensionData[key] = userThemeData[key];
            }
        }
        let extendedTheme = "ms.vss-web.vsts-theme";
        if (selectedTheme) {
            extendedTheme = selectedTheme.id;
        }
        const extensionJson = {
            id: themeGeneratorId,
            type: "ms.vss-web.theme",
            targets: [".vsts-theme-root"],
            properties: {
                extends: extendedTheme,
                name: Resources.CustomThemeName,
                data: extensionData
            }
        };
        return JSON.stringify(extensionJson, null, 4);
    }

    private onAutoComputeShadesChanged = (event: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>, newValue: boolean): void => {
        this.setState({ autoComputeShades: newValue });
    };
}

Components.add("theme-generator-view", ThemeGenerator);
